import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import {
  NavigationModule,
} from '@progress/kendo-angular-navigation';
import { KENDO_ICONS, SVGIconModule } from '@progress/kendo-angular-icons';
import {
  KENDO_BUTTONS,
  ListItemModel,
} from '@progress/kendo-angular-buttons';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
} from '@angular/router';

import {
  arrowRotateCcwIcon,
  homeIcon,
  SVGIcon,
  plusIcon,
  bellIcon,
  userIcon,
  chevronRightIcon,
  logoutIcon,
  downloadIcon,
} from '@progress/kendo-svg-icons';
import { filter, map, mergeMap, Subscription } from 'rxjs';
import { EngagementComponent } from '../engagement/engagement.component';
import { RefreshGridService } from '../../shared/services/refresh-grid.service';
import { ClientService } from '../../services/client.service';
import { AuthService } from '../../shared/auth/auth.service';
import { RequestComponent } from '../request/request.component';
import { AuthUserService } from '../../shared/auth/auth-user.service';
import { Roles } from '../../constants/roles';
import { EngagementrequestComponent } from '../shared/request-panel/request-wrapper/engagement-request-wrapper/engagementrequest/engagementrequest.component';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { RouterModule } from '@angular/router';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    KENDO_LAYOUT,
    KENDO_ICONS,
    SVGIconModule,
    KENDO_BUTTONS,
    NavigationModule,
    EngagementrequestComponent,
    NotificationBellComponent,
    RouterModule,
    KENDO_TOOLTIPS
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  public homeIcon: SVGIcon = homeIcon;
  public rotateIcon: SVGIcon = arrowRotateCcwIcon;
  public plusIcon: SVGIcon = plusIcon;
  public bellIcon: SVGIcon = bellIcon;
  public userIcon: SVGIcon = userIcon;
  public downloadIcon: SVGIcon = downloadIcon;
  public chevronRightIcon: SVGIcon = chevronRightIcon;
  public logoutIcon: SVGIcon = logoutIcon;
  public margin = { horizontal: -30, vertical: -15 };
  public clientName: any = '';
  public isRequestAndApproveEngagementPopupOpen = false;

  private readonly routesData: Subscription = new Subscription();
  public showClientPopup: boolean = false;
  public showEngagementPopup: boolean = false;

  public UserProfile: ListItemModel[] = [
    {
      text: 'Log Out',
      svgIcon: logoutIcon,
    },
  ];

  private readonly subscriptions: Subscription = new Subscription();

  @ViewChild('anchor', { static: true }) anchor!: ElementRef;
  @ViewChild(EngagementComponent) engagementComponent!: EngagementComponent;
  @ViewChild(RequestComponent) requestComponent!: RequestComponent;
  loggedInUserRole: string = '';
  userRoles = Roles;
  userProfile: any;
  loggedInUserDetails: any;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly refreshGridService = inject(RefreshGridService);
  private readonly authService = inject(AuthService);
  private readonly authUserService = inject(AuthUserService);
  private readonly clientService = inject(ClientService);

  public clientId: string = '';
  breadcrumbs: Array<{ label: string; url: string }> = [];

  // Allowed route patterns to prevent open redirect
  private readonly allowedRoutePatterns = [
    /^\/dashboard$/i,
    /^\/Client\/\d+$/,
    /^\/Client\/\d+\/EngagementId\/[a-zA-Z0-9-]+$/,
    /^\/Client\/\d+\/EngagementId\/[a-zA-Z0-9-]+\/moduleId\/[a-zA-Z0-9-]+$/,
    /^\/Request$/i,
    /^\/Client\/\d+\/EngagementId\/[a-zA-Z0-9-]+\/RequestModule$/,
    /^\/RequestEngagementSuccess$/i,
    /^\/module\/module-catalog\/manifest-creator$/i
  ];

  ngOnInit(): void {
    if (!localStorage.getItem('loggedInUserRole')) {
      this.authUserService.loggedInUserProfile.subscribe((userRole) => {
        this.loggedInUserRole = userRole;
      });
    } else
      this.loggedInUserRole = localStorage.getItem('loggedInUserRole') ?? '';
    this.showClientPopup = false;
    this.showEngagementPopup = false;

    this.subscriptions.add(
      this.refreshGridService.clientAdded$.subscribe(() => {
        this.onClientAdded();
      })
    );
    //calls breacrumb on load
    this.loadBreadcrumb(this.route);

    //calls loadBreadcrumb on every navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.loadBreadcrumb(this.route);
      });

    this.userProfile = JSON.parse(localStorage.getItem('userProfile') ?? 'null');
    this.loggedInUserDetails = JSON.parse(localStorage.getItem('loggedInUserDetails') ?? 'null');
    if (this.loggedInUserDetails) {
      this.loggedInUserDetails.userInitials = this.loggedInUserDetails?.firstName[0] + this.loggedInUserDetails?.lastName[0];
    } else {
      this.loggedInUserDetails.userInitials = 'N/A';
    }
  }

  //breadcrumb logic
  loadBreadcrumb(route: ActivatedRoute) {
    let currentRoute = route.root;
    const pathSegments: string[] = [];

    //get segments based on route
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      if (currentRoute.snapshot.url.length) {
        pathSegments.push(...currentRoute.snapshot.url.map((seg) => seg.path));
      }
    }

    let clientId = '';
    let engagementId = '';
    let moduleId = '';

    //store ids
    pathSegments.forEach((segment, index) => {
      if (segment === 'Client' && index + 1 < pathSegments.length) {
        clientId = pathSegments[index + 1];
      } else if (
        segment === 'EngagementId' &&
        index + 1 < pathSegments.length
      ) {
        engagementId = pathSegments[index + 1];
      } else if (segment === 'moduleId' && index + 1 < pathSegments.length) {
        moduleId = pathSegments[index + 1];
      }
    });

    //store names - sanitize to prevent XSS
    let clientName = this.sanitizeSessionStorageValue(sessionStorage.getItem('clientName'));
    let engagementName = this.sanitizeSessionStorageValue(sessionStorage.getItem('engagementName'));
    let moduleName = this.sanitizeSessionStorageValue(sessionStorage.getItem('moduleName'));

    // If names are missing but IDs are present, fetch from API
    if ((!clientName && clientId) || (!engagementName && engagementId)) {
      this.clientService.getClientsWithDetails().subscribe({
        next: (results) => {
          const clientRecord = results['data'].find((data: any) => clientId == data.clientId);
          const clientEngagementRecord = clientRecord?.engagements?.find(
            (data: any) => engagementId == data.engagementId
          );
          if (clientRecord) {
            sessionStorage.setItem('clientId', String(clientRecord.clientId));
            sessionStorage.setItem('clientName', String(clientRecord.clientName));
            clientName = clientRecord.clientName;
          }
          if (clientEngagementRecord) {
            sessionStorage.setItem('engagementId', String(clientEngagementRecord.engagementId));
            sessionStorage.setItem('engagementName', String(clientEngagementRecord.name));
            engagementName = clientEngagementRecord.name;
          }
          // After client/engagement, check for module
          if (moduleId && !moduleName) {
            this.fetchAndSetModuleName(moduleId, pathSegments, clientId, engagementId, clientName, engagementName);
          } else {
            this.setBreadcrumbs(pathSegments, clientId, engagementId, clientName, engagementName, moduleId, moduleName!);
          }
        },
        error: (error) => {
          // Optionally handle error, e.g. show default breadcrumb or error message
          if (moduleId && !moduleName) {
            this.fetchAndSetModuleName(moduleId, pathSegments, clientId, engagementId, clientName, engagementName);
          } else {
            this.setBreadcrumbs(pathSegments, clientId, engagementId, clientName, engagementName, moduleId, moduleName!);
          }
        },
      });
    } else if (moduleId && !moduleName) {
      // Only module name is missing, fetch module details
      this.fetchAndSetModuleName(moduleId, pathSegments, clientId, engagementId, clientName, engagementName);
    } else {
      this.setBreadcrumbs(pathSegments, clientId, engagementId, clientName, engagementName, moduleId, moduleName!);
    }
  }

  private fetchAndSetModuleName(
    moduleId: string,
    pathSegments: string[],
    clientId: string,
    engagementId: string,
    clientName: string | null,
    engagementName: string | null
  ) {
    this.clientService.getClientEngagementsWithDetails(Number(clientId)).subscribe({
      next: (engagementData: any) => {
        if (engagementData) {
          const engagementRecord = engagementData['data'].find((mod: any) => mod.engagementId == engagementId);
          const moduleRecord = engagementRecord?.modules?.find(
            (data: any) => moduleId == data.id
          );
          if (moduleRecord) {
            sessionStorage.setItem('moduleId', String(moduleRecord.id));
            sessionStorage.setItem('moduleName', String(moduleRecord.name));
            this.setBreadcrumbs(
              pathSegments,
              clientId,
              engagementId,
              clientName,
              engagementName,
              moduleId,
              moduleRecord.name
            );
          } else {
            this.setBreadcrumbs(
              pathSegments,
              clientId,
              engagementId,
              clientName,
              engagementName,
              moduleId,
              'Module'
            );
          }
        }
      },
      error: () => {
        this.setBreadcrumbs(
          pathSegments,
          clientId,
          engagementId,
          clientName,
          engagementName,
          moduleId,
          'Module'
        );
      }
    });
  }

  // Validate URL to prevent open redirect attacks
  private isValidInternalUrl(url: string): boolean {
    if (!url || url.trim() === '') {
      return false;
    }

    // Ensure URL starts with / and doesn't contain protocol or double slashes
    if (!url.startsWith('/') || url.includes('://') || url.includes('//')) {
      return false;
    }

    // Check against allowed patterns
    return this.allowedRoutePatterns.some(pattern => pattern.test(url));
  }

  // Sanitize ID to ensure it contains only alphanumeric characters and hyphens
  private sanitizeId(id: string): string {
    if (!id) {
      return '';
    }
    // Allow only alphanumeric characters, hyphens, and underscores
    return id.replace(/[^a-zA-Z0-9-_]/g, '');
  }

  // Update setBreadcrumbs to accept moduleId and moduleName
  setBreadcrumbs(
    pathSegments: string[],
    clientId: string,
    engagementId: string,
    clientName: any,
    engagementName: any,
    moduleId?: string,
    moduleName?: string
  ) {
    const breadcrumbs = [{ label: 'Dashboard', url: '/dashboard' }];

    // Sanitize IDs to prevent injection
    const sanitizedClientId = this.sanitizeId(clientId);
    const sanitizedEngagementId = this.sanitizeId(engagementId);
    const sanitizedModuleId = moduleId ? this.sanitizeId(moduleId) : '';

    pathSegments.forEach((segment, index) => {
      if (segment === 'Client' && sanitizedClientId) {
        const url = `/Client/${sanitizedClientId}`;
        if (this.isValidInternalUrl(url)) {
          breadcrumbs.push({
            label: clientName || 'Client',
            url: url,
          });
        }
      } else if (segment === 'EngagementId' && sanitizedEngagementId) {
        const url = `/Client/${sanitizedClientId}/EngagementId/${sanitizedEngagementId}`;
        if (this.isValidInternalUrl(url)) {
          breadcrumbs.push({
            label: engagementName || 'Engagement',
            url: url,
          });
        }
      } else if (segment === 'moduleId' && sanitizedModuleId) {
        const url = `/Client/${sanitizedClientId}/EngagementId/${sanitizedEngagementId}/moduleId/${sanitizedModuleId}`;
        if (this.isValidInternalUrl(url)) {
          breadcrumbs.push({
            label: moduleName || 'Module',
            url: url,
          });
        }
      } else if (segment === 'Request') {
        const url = '/Request';
        if (this.isValidInternalUrl(url)) {
          breadcrumbs.push({ label: 'Requests', url: url });
        }
      } else if (segment === 'RequestModule') {
        const url = `/Client/${sanitizedClientId}/EngagementId/${sanitizedEngagementId}/RequestModule`;
        if (this.isValidInternalUrl(url)) {
          breadcrumbs.push({
            label: 'Request Module',
            url: url,
          });
        }
      } else if (segment === 'RequestEngagementSuccess') {
        const url = '/RequestEngagementSuccess';
        if (this.isValidInternalUrl(url)) {
          breadcrumbs.push({
            label: 'Request Engagement Success',
            url: url,
          });
        }
      }
      else if (segment === 'manifest-creator') {
        const url = '/module/module-catalog/manifest-creator';
        if (this.isValidInternalUrl(url)) {
          breadcrumbs.push({
            label: 'Manifest Creator',
            url: url,
          });
        }
      }
    });

    this.breadcrumbs = breadcrumbs;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onNewEngagementClick(): void {
    this.isRequestAndApproveEngagementPopupOpen = true;
  }

  public closeClientPopup() {
    this.showClientPopup = false;
  }

  public closeEngagementPopup() {
    this.showEngagementPopup = false;
  }

  onRequestAndApproveEngagementPopupClose(): void {
    this.isRequestAndApproveEngagementPopupOpen = false;

    //this.myElement.nativeElement.style.backgroundColor = '#f4f4f4'; // Correct syntax
  }

  enableEngageButton(): boolean {
    if (this.router.url.includes('/Client')) return true;
    return false;
  }

  onClientAdded(): void { }

  public onEngagementAdded(): void {
    if (this.engagementComponent) {
      this.engagementComponent.refreshEngagementGrid();
    }
  }

  public onRequestAndApproveButton(): void {
    //if (this.requestComponent) {
    //this.requestComponent.onEngagemetApproveOrDenyButton();
    //}
  }

  // Method to handle the click event
  public onDropdownButtonClick(item: ListItemModel): void {
    if (item.text === 'Log Out') {
      this.logout(); // Call the logout method when "Log Out" is clicked
    }
  }

  // Define the logout method
  private logout(): void {
    this.authService.signOut();
  }

  downloadClientUsageReportForSupportUsers(): void {
    this.clientService.downloadClientUsageReport().subscribe((result) => {
      const now = new Date();
      const month = now.getUTCMonth() + 1;
      const day = now.getUTCDate();
      const year = now.getUTCFullYear();
      const datePart = `${month}-${day}-${year}`;
      const filename = `Risk Platform Usage Report - ${datePart}.csv`;

      const csvLink = document.createElement('a');
      const url = URL.createObjectURL(new Blob([result.body], { type: result.body.type }));
      csvLink.href = url;
      csvLink.download = filename;
      csvLink.click();
    });
  }

  /**
 * Sanitizes values retrieved from sessionStorage to prevent XSS attacks.
 * Removes HTML tags and special characters that could be used for injection.
 */
  private sanitizeSessionStorageValue(value: string | null): string | null {
    if (!value) {
      return null;
    }

    // Remove HTML tags and potentially dangerous characters
    const sanitized = value
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[<>\"']/g, '') // Remove dangerous characters
      .trim();

    return sanitized || null;
  }
}
